/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { generateErrorToast } from '@g17eco/molecules/toasts';
import LoadingButton from '@g17eco/molecules/button/LoadingButton';
import { useGetDataTransferDashboardQuery, useListSubConnectionsQuery, useTriggerDataTransferSyncMutation, useRegenerateDataTransferPendingMutation } from '@api/initiative-app-integrations';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { getInitiativesTreeMapById } from '@selectors/initiativeTree';
import { CTAdminBreadcrumbs } from '../../../../components/breadcrumbs/CTAdminBreadcrumbs';
import { ROUTES } from '@constants/routes';
import { INITIATIVE, QUESTION } from '@constants/terminology';
import { generateUrl } from '@routes/util';
import { StatusBadge, getRowBorderColor as getStatusBorderColor } from '@g17eco/atoms/status-badge';
import { Timestamp } from '@g17eco/atoms/timestamp/Timestamp';
import { type SubsidiaryGroup, getJobSubsidiaryGroups, isRefreshStuck, resolveGroupStatus, getGroupReviewProgress, latestJobUpdate, buildSubsidiaryMap } from './utils';
import type { DataTransferDashboardResponse } from '@g17eco/types/data-transfer';
import type { InitiativePlain } from '@g17eco/types/initiative';
import { InitiativeBreadcrumbs, buildAncestryPath } from '@g17eco/atoms/initiative-breadcrumbs';


const SummaryCard = ({ label, value, sublabel, icon, iconClass }: {
  label: string;
  value: string | number;
  sublabel: string;
  icon: string;
  iconClass?: string;
}) => (
  <div className='col'>
    <div className='p-3 whiteBoxContainer'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        <i className={`fa ${icon} ${iconClass ?? 'text-ThemePrimaryMedium'}`} />
        <span className='small text-ThemeTextLight text-uppercase fw-bold'>{label}</span>
      </div>
      <div className='h2 fw-bold mb-1'>{value}</div>
      <div className='small text-ThemeTextLight'>{sublabel}</div>
    </div>
  </div>
);

const ReviewProgress = ({ group }: { group: SubsidiaryGroup }) => {
  const { reviewed, actionable } = getGroupReviewProgress(group);
  if (!group.allGenerated || actionable === 0) {
    return <span className='text-ThemeTextLight'>—</span>;
  }
  return (
    <span className={reviewed === actionable ? 'text-ThemeSuccessMedium' : 'text-ThemeWarningMedium'}>
      {reviewed}/{actionable}
    </span>
  );
};

const RETRY_BLOCKED_TOOLTIP = 'Wait for generation to finish before retrying';

const StuckRetryButton = ({ group, initiativeId, code, dataTransferId, disabled }: {
  group: SubsidiaryGroup;
  initiativeId: string;
  code: string;
  dataTransferId: string;
  disabled?: boolean;
}) => {
  const [regenerate] = useRegenerateDataTransferPendingMutation();
  const [isRetrying, setIsRetrying] = useState(false);

  const stuckJobs = group.jobs.filter((j) => isRefreshStuck(j));

  const handleRetry = async () => {
    setIsRetrying(true);
    await Promise.allSettled(
      stuckJobs.map((j) => regenerate({ initiativeId, code, dataTransferId, jobId: j._id }).unwrap()),
    );
    setIsRetrying(false);
  };

  return (
    <LoadingButton
      color='outline-warning'
      size='sm'
      loading={isRetrying}
      disabled={disabled}
      title={disabled ? RETRY_BLOCKED_TOOLTIP : undefined}
      onClick={handleRetry}
    >
      <i className='fa fa-refresh me-1' />Retry ({stuckJobs.length})
    </LoadingButton>
  );
};

const ActionCell = ({ group, initiativeId, code, dataTransferId, isAnyGenerating }: {
  group: SubsidiaryGroup;
  initiativeId: string;
  code: string;
  dataTransferId: string;
  isAnyGenerating: boolean;
}) => {
  if (!group.allGenerated) {
    const isFullyStuck = group.stuckJobCount > 0 && group.stuckJobCount === group.ungeneratedJobCount;
    if (isFullyStuck) {
      return (
        <StuckRetryButton
          group={group}
          initiativeId={initiativeId}
          code={code}
          dataTransferId={dataTransferId}
          disabled={isAnyGenerating}
        />
      );
    }
    const allQueued = group.queuedJobCount === group.ungeneratedJobCount;
    const total = group.jobs.length;
    const generated = total - group.ungeneratedJobCount;
    return (
      <span className='d-flex align-items-center gap-1 text-ThemeTextLight'>
        <i className={allQueued ? 'fal fa-clock' : 'fas fa-spinner fa-spin'} style={{ fontSize: '0.75rem' }} />
        {allQueued ? `Waiting to start (${group.queuedJobCount})` : `Processing (${generated}/${total})`}
      </span>
    );
  }

  if (group.jobs.length === 0) {
    return null;
  }

  const reviewUrl = generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFER_REVIEW, {
    initiativeId,
    code,
    dataTransferId,
    subsidiaryId: group.subsidiaryId,
  });

  if (group.errorCount > 0) {
    return (
      <Link to={reviewUrl} className='btn btn-sm btn-danger'>
        Fix Issues <i className='fa fa-long-arrow-right ms-1' />
      </Link>
    );
  }

  const btnClass = group.pendingCount > 0 ? 'btn-primary' : 'btn-outline-secondary';

  return (
    <Link to={reviewUrl} className={`btn btn-sm ${btnClass}`}>
      Review <i className='fa fa-long-arrow-right ms-1' />
    </Link>
  );
};

const SubsidiaryRow = ({ group, initiativeId, code, dataTransferId, subsidiaryMap, treeMap, isAnyGenerating }: {
  group: SubsidiaryGroup;
  initiativeId: string;
  code: string;
  dataTransferId: string;
  subsidiaryMap: Map<string, string>;
  treeMap: Map<string, InitiativePlain>;
  isAnyGenerating: boolean;
}) => {
  const history = useHistory();
  const subsidiaryName = subsidiaryMap.get(group.subsidiaryId) ?? group.subsidiaryId;
  const ancestryPath = buildAncestryPath(group.subsidiaryId, treeMap);
  const status = resolveGroupStatus(group);
  const canNavigate = group.allGenerated && group.jobs.length > 0;
  const url = canNavigate
    ? generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFER_REVIEW, { initiativeId, code, dataTransferId, subsidiaryId: group.subsidiaryId })
    : undefined;

  return (
    <tr
      className={`align-middle ${canNavigate ? 'cursor-pointer' : ''}`}
      style={{ borderLeft: `3px solid ${getStatusBorderColor(status)}` }}
      onClick={url ? () => history.push(url) : undefined}
    >
      <td className='py-3'>
        <div className='fw-bold'>{subsidiaryName}</div>
        <InitiativeBreadcrumbs path={ancestryPath} />
      </td>
      <td className='py-3 text-ThemeTextMedium'>{group.jobs.length}</td>
      <td className='py-3 text-ThemeTextMedium'>{group.totalTasks}</td>
      <td className='py-3'>
        <ReviewProgress group={group} />
      </td>
      <td className='py-3'>
        {group.allGenerated ? (
          <Timestamp value={latestJobUpdate(group.jobs)} format='datetime' className='small text-ThemeTextLight' />
        ) : (
          <span className='text-ThemeTextLight'>—</span>
        )}
      </td>
      <td className='py-3'>
        <StatusBadge status={status} />
      </td>
      <td className='py-3 text-end' onClick={(e) => e.stopPropagation()}>
        <ActionCell
          group={group}
          initiativeId={initiativeId}
          code={code}
          dataTransferId={dataTransferId}
          isAnyGenerating={isAnyGenerating}
        />
      </td>
    </tr>
  );
};

const DashboardContent = ({ data, initiativeId, code, dataTransferId, subsidiaryMap, treeMap, onRefresh }: {
  data: DataTransferDashboardResponse;
  initiativeId: string;
  code: string;
  dataTransferId: string;
  subsidiaryMap: Map<string, string>;
  treeMap: Map<string, InitiativePlain>;
  onRefresh: () => void;
}) => {
  const [triggerSync] = useTriggerDataTransferSyncMutation();
  const [isSyncingAll, setIsSyncingAll] = useState(false);

  const { dataTransfer, jobs } = data;
  const groups = getJobSubsidiaryGroups(dataTransfer, jobs);
  const totals = groups.reduce(
    (acc, g) => {
      acc.pending += g.pendingCount;
      acc.synced += g.syncedCount;
      acc.skipped += g.skippedCount;
      acc.tasks += g.totalTasks;
      acc.errors += g.errorCount;
      acc.approved += g.approvedCount;
      return acc;
    },
    { pending: 0, synced: 0, skipped: 0, tasks: 0, errors: 0, approved: 0 },
  );
  const { pending: totalPending, synced: totalSynced, skipped: totalSkipped, tasks: totalTasks, errors: totalErrors, approved: totalApproved } = totals;
  const actionableCount = totalTasks - totalSkipped;
  const syncHealth = actionableCount > 0 ? `${Math.round((totalSynced / actionableCount) * 100)}%` : '—';
  const readyCount = groups.filter((g) => g.allGenerated).length;
  const created = new Date(dataTransfer.created).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const syncableJobIds = groups
    .filter((g) => g.allGenerated && g.approvedCount > 0)
    .flatMap((g) => g.jobs.map((j) => j._id));
  // True when any group has live (non-stuck) generation in flight: queued/
  // processing jobs without metric tasks yet, or a generate task re-running on
  // an already-generated group. Fully-stuck groups don't count — they hold no
  // parallel slot, so retries against them are safe.
  const isAnyGenerating = groups.some(
    (g) => g.refreshingJobCount > 0 || g.ungeneratedJobCount - g.stuckJobCount > 0,
  );

  const handleSyncAll = async () => {
    setIsSyncingAll(true);
    const results = await Promise.allSettled(
      syncableJobIds.map((jobId) => triggerSync({ initiativeId, code, dataTransferId, jobId }).unwrap()),
    );
    const failures = results.filter((r) => r.status === 'rejected');
    if (failures.length > 0) {
      generateErrorToast({ message: `${failures.length} of ${syncableJobIds.length} sync jobs failed.` });
    }
    setIsSyncingAll(false);
  };

  return (
    <>
      <DashboardSectionTitle
        title={<>{dataTransfer.name} <FeatureStability stability='internal' /></>}
        subtitle={<span className='text-capitalize'>{dataTransfer.frequency} · Created {created}</span>}
        buttons={[
          <button key='refresh' className='btn btn-outline-secondary' onClick={onRefresh}>
            <i className='fa fa-refresh me-1' />Refresh
          </button>,
          ...(syncableJobIds.length > 0 ? [
            <LoadingButton
              key='sync-all'
              color='primary'
              loading={isSyncingAll}
              disabled={isAnyGenerating}
              title={isAnyGenerating ? 'Wait for generation to finish before syncing' : undefined}
              onClick={handleSyncAll}
            >
              <i className='fal fa-cloud-upload me-1' />Sync {totalApproved} approved rows
            </LoadingButton>,
          ] : []),
        ]}
      />

      <div className='dashboard-row mb-3'>
        <div className='row g-3'>
          <SummaryCard label={INITIATIVE.CAPITALIZED_PLURAL} value={groups.length} sublabel={`${readyCount} ready for review`} icon='fa-building' />
          <SummaryCard
            label='Sync Health'
            value={syncHealth}
            sublabel={totalSkipped > 0 ? `${totalSynced} synced · ${totalSkipped} skipped` : `${totalSynced} of ${totalTasks} ${QUESTION.PLURAL}`}
            icon='fa-bolt'
          />
          <SummaryCard label='Errors' value={totalErrors} sublabel='Require attention' icon='fa-exclamation-triangle' iconClass='text-ThemeDangerMedium' />
          <SummaryCard label='Pending Review' value={totalPending} sublabel='Awaiting decision' icon='fa-clock-o' iconClass='text-ThemeWarningMedium' />
        </div>
      </div>

      <DashboardSection padding={0} paddingInternal={0}>
        <table className='table mb-0'>
        <thead>
          <tr className='text-ThemeTextLight text-uppercase small'>
            <th className='ps-4 py-3'>{INITIATIVE.CAPITALIZED_SINGULAR}</th>
            <th className='py-3'>Periods</th>
            <th className='py-3'>{QUESTION.CAPITALIZED_PLURAL}</th>
            <th className='py-3'>Reviewed</th>
            <th className='py-3'>Snapshot</th>
            <th className='py-3' style={{ minWidth: 100 }}>Status</th>
            <th className='py-3' style={{ minWidth: 120 }}></th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <SubsidiaryRow
              key={group.subsidiaryId}
              group={group}
              initiativeId={initiativeId}
              code={code}
              dataTransferId={dataTransferId}
              subsidiaryMap={subsidiaryMap}
              treeMap={treeMap}
              isAnyGenerating={isAnyGenerating}
            />
          ))}
        </tbody>
      </table>
      </DashboardSection>
    </>
  );
};

export const DataTransferDashboard = () => {
  const { code, initiativeId, dataTransferId } = useParams<{ code: string; initiativeId: string; dataTransferId: string }>();
  const isUserStaff = useAppSelector(isStaff);
  const treeMap = useAppSelector(getInitiativesTreeMapById);

  const query = useGetDataTransferDashboardQuery(
    { initiativeId, code, dataTransferId },
    { skip: !isUserStaff || !dataTransferId, pollingInterval: 10000, refetchOnMountOrArgChange: true },
  );
  const subConnectionsQuery = useListSubConnectionsQuery(
    { initiativeId, code },
    { skip: !isUserStaff },
  );

  if (!isUserStaff) {
    return null;
  }

  return (
    <Dashboard>
      <div className='pl-2 pb-4'>
        <CTAdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[
            { label: 'Connections', url: generateUrl(ROUTES.APP_INTEGRATIONS, { initiativeId }) },
            { label: code, url: generateUrl(ROUTES.APP_INTEGRATIONS_VIEW, { initiativeId, code }) },
            { label: 'Data Transfers', url: generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFERS, { initiativeId, code }) },
            { label: 'Dashboard' },
          ]}
        />
      </div>
      <QueryWrapper query={query} onFetching={() => <></>} onSuccess={(data) => {
        const subsidiaryMap = buildSubsidiaryMap(subConnectionsQuery.data ?? []);
        return (
          <DashboardContent
            data={data}
            treeMap={treeMap}
            initiativeId={initiativeId}
            code={code}
            dataTransferId={dataTransferId}
            subsidiaryMap={subsidiaryMap}
            onRefresh={() => query.refetch()}
          />
        );
      }} />
    </Dashboard>
  );
};
